import { z } from "zod";
import type { CampaignConfiguration } from "./campaign-goal-run.ts";
import { quoteOccurs } from "./quote-span.ts";

/**
 * Materials and references (`docs/2026-10-02-materials-and-references.md`, plan step 6): what a reply
 * does when the lead asks for something the seller can give before a call (a deck, a case study).
 *
 * Pure: the kind is matched in the lead's own words, the campaign's answer arrives as a typed view the
 * worker read from the app, and the decision is a value. This module runs inside the workflow, so it
 * holds no clock, no I/O and no Node builtins (`scripts/workflow-safety.ts`).
 */

export const MATERIALS_PATCH = "materials-v1";
export const MATERIALS_OFF_PATCH = "materials-off-v1";
export const REFERENCE_REQUEST_PATCH = "reference-request-v1";
export const REFERENCE_REQUEST_OFF_PATCH = "reference-request-off-v1";
export const MATERIAL_ATTACHMENT_WORDING_PATCH = "material-attachment-wording-v1";
export const MATERIAL_ATTACHMENT_WORDING_OFF_PATCH = "material-attachment-wording-off-v1";

/** The run action of a turn that stopped for the card, a free-text type as every rep task is. */
export const PROVIDE_MATERIAL_ACTION = "provide_material";
/** The plan step id prefix of the card's `manual` step. */
export const MATERIAL_STEP_PREFIX = "material-request";

/** The materials rules' own version: the run's manifest digest covers the default rule set only. */
const MATERIALS_RULES_VERSION = 1;

export const MaterialKindSchema = z.enum([
  "deck",
  "case_study",
  "one_pager",
  "video",
  "pricing",
  "proposal",
]);
export type MaterialKind = z.infer<typeof MaterialKindSchema>;

/** The kind as a reply and the card name it. */
export const MATERIAL_KIND_LABEL: Record<MaterialKind, string> = {
  deck: "deck",
  case_study: "case study",
  one_pager: "one-pager",
  video: "video",
  pricing: "pricing",
  proposal: "proposal",
};

/**
 * Whole words, English only. A video call is not a video; "recording" and "PDF" are left out because
 * they name a medium, not a kind of material (the design's "The kind list").
 */
const KIND_PATTERNS: readonly [MaterialKind, RegExp][] = [
  ["deck", /\b(?:pitch\s+deck|slide\s+deck|deck|slides|presentation)\b/iu],
  ["case_study", /\b(?:case[\s-]+stud(?:y|ies)|success\s+stor(?:y|ies))\b/iu],
  ["one_pager", /\b(?:one[\s-]?pager|brochure|overview\s+document)\b/iu],
  ["video", /\b(?:demo\s+)?videos?\b(?!\s*(?:call|chat|meeting|conference))/iu],
  ["pricing", /\b(?:pricing(?:\s+sheet)?|price\s+list|rate\s+card)\b/iu],
  ["proposal", /\b(?:full\s+)?proposals?\b/iu],
];

/** Kinds that stay behind the call unless the campaign marks its material shareable. */
const BEHIND_THE_CALL: ReadonlySet<MaterialKind> = new Set(["pricing", "proposal"]);

function materialKindsIn(text: string): MaterialKind[] {
  return KIND_PATTERNS.filter(([, pattern]) => pattern.test(text)).map(([kind]) => kind);
}

/** A request the materials rule acts on: one speech act, the kinds the lead named (none is vague). */
export type MaterialRequest = { speechActId: string; kinds: MaterialKind[] };

/**
 * The `send_materials` acts the planner did not answer, with the kinds named in the lead's words.
 * With one such act the lead's authored message is read, because the planner usually paraphrases;
 * with several, each act's text, and only where that text occurs in the message.
 */
export function materialRequests(input: {
  bindings: readonly { ask: string; speech_act_ids: readonly string[] }[];
  acts: readonly { id: string; text: string; resolution: string }[];
  authoredText: string | null;
}): MaterialRequest[] {
  const bound = new Set(
    input.bindings
      .filter((binding) => binding.ask === "send_materials")
      .flatMap((binding) => binding.speech_act_ids),
  );
  const acts = input.acts.filter((act) => bound.has(act.id) && act.resolution !== "answer");
  const authored = input.authoredText ?? "";
  return acts.map((act) => ({
    speechActId: act.id,
    kinds:
      acts.length === 1
        ? materialKindsIn(authored)
        : authored && quoteOccurs(authored, act.text)
          ? materialKindsIn(act.text)
          : [],
  }));
}

/** What the campaign has said about each kind, as the worker read it from the app. */
const MaterialRowSchema = z.strictObject({
  material_id: z.uuid(),
  kind: MaterialKindSchema,
  state: z.enum(["present", "confirmed_absent", "to_call"]),
  delivery: z.enum(["link", "attachment"]).nullable(),
  url: z.url({ protocol: /^https$/ }).nullable(),
  file: z
    .strictObject({
      sha256: z.string().regex(/^[a-f0-9]{64}$/u),
      file_name: z.string().min(1).max(255),
      media_type: z.string().min(1),
      bytes: z.number().int().positive(),
    })
    .nullable(),
  shareable: z.boolean(),
});
type MaterialRow = z.infer<typeof MaterialRowSchema>;

export const MaterialsViewSchema = z.strictObject({
  rows: z.array(MaterialRowSchema),
  turn_overrides: z.array(
    z.strictObject({ kind: MaterialKindSchema, choice: z.literal("to_call_reply") }),
  ),
});
export type MaterialsView = z.infer<typeof MaterialsViewSchema>;

export interface MaterialsRequest {
  readonly workspaceId: string | number;
  readonly campaignId: number;
  readonly conversationId: string;
  readonly triggerId: number;
}

const MaterialRequirementSchema = z.strictObject({
  material_id: z.uuid(),
  blob_sha256: z
    .string()
    .regex(/^[a-f0-9]{64}$/u)
    .nullable(),
  delivery: z.literal("attachment"),
  file_name: z.string().min(1).max(255).nullable(),
});
export type MaterialRequirement = z.infer<typeof MaterialRequirementSchema>;

const MaterialEntrySchema = z.strictObject({
  speech_act_id: z.string().nullable(),
  kind: MaterialKindSchema.nullable(),
  outcome: z.enum([
    "card",
    "share_link",
    "attach",
    "absent",
    "to_call",
    "vague",
    "attachment_wording",
  ]),
  sentence: z.string().min(1).nullable(),
  resource_id: z
    .string()
    .regex(/^seller_resource:\d+$/u)
    .nullable(),
  requirement: MaterialRequirementSchema.nullable(),
});
export type MaterialEntry = z.infer<typeof MaterialEntrySchema>;

/**
 * The run's record, in its own sidecar file (`run-persistence.ts`), only on runs the materials rules
 * touched. A resumed drive reads its outcome from here and never asks a switch or the app again.
 */
export const MaterialsRecordSchema = z.strictObject({
  version: z.literal(1),
  rules_version: z.literal(MATERIALS_RULES_VERSION),
  references_excluded: z.boolean(),
  entries: z.array(MaterialEntrySchema),
});
export type MaterialsRecord = z.infer<typeof MaterialsRecordSchema>;

export function emptyMaterialsRecord(): MaterialsRecord {
  return {
    version: 1,
    rules_version: MATERIALS_RULES_VERSION,
    references_excluded: false,
    entries: [],
  };
}

/** The host's sentences. Fixed text, for Dawid's yes; the rule requires each verbatim, once. */
const VAGUE_SENTENCE = "We can go through the details on the call.";
export function attachSentence(kind: MaterialKind): string {
  return `I've attached our ${MATERIAL_KIND_LABEL[kind]}.`;
}
function absentSentence(kind: MaterialKind): string {
  return `We don't have a ${MATERIAL_KIND_LABEL[kind]} to send.`;
}
function toCallSentence(kind: MaterialKind): string {
  return kind === "pricing"
    ? "We can go through pricing on the call."
    : `We can go through the ${MATERIAL_KIND_LABEL[kind]} on the call.`;
}
/** The reference question's fixed text, replacing the planner's wording of that one question. */
export const REFERENCE_QUESTION = "What should replies say when a lead asks for references?";

/**
 * Whether the campaign has a call to point to: the deferral rule's own guard
 * (`deferred-questions.ts`), a pinned meeting campaign that permits offering or linking a meeting.
 */
export function campaignHasCall(campaign: CampaignConfiguration): boolean {
  return (
    campaign.status === "pinned" &&
    ["meeting", "phone_call"].includes(campaign.goal.kind) &&
    !!campaign.policy?.permitted_actions.some((action) =>
      campaign.goal.kind === "phone_call"
        ? action === "collect_phone_call"
        : action === "offer_meeting" || action === "share_booking_link",
    )
  );
}

export type MaterialDecision =
  /** No draft: the rep answers the card. `kinds` are the unknown ones the card asks about. */
  | { move: "card"; kinds: MaterialKind[]; entries: MaterialEntry[] }
  /** A draft, with the host's sentences, links and requirements. Empty entries change nothing. */
  | { move: "draft"; entries: MaterialEntry[] };

const entry = (
  speechActId: string,
  kind: MaterialKind | null,
  outcome: MaterialEntry["outcome"],
  rest: Partial<Pick<MaterialEntry, "sentence" | "resource_id" | "requirement">> = {},
): MaterialEntry => ({
  speech_act_id: speechActId,
  kind,
  outcome,
  sentence: rest.sentence ?? null,
  resource_id: rest.resource_id ?? null,
  requirement: rest.requirement ?? null,
});

/**
 * One decision per request and kind, from the campaign's rows. The card wins over a draft for the
 * whole turn; a kind the campaign cannot answer without a call keeps today's path when it has none.
 */
export function decideMaterials(input: {
  requests: readonly MaterialRequest[];
  view: MaterialsView | null;
  hasCall: boolean;
  sellerResourceUrls: readonly string[];
}): MaterialDecision {
  const entries: MaterialEntry[] = [];
  const unknown = new Set<MaterialKind>();
  const rows = new Map((input.view?.rows ?? []).map((row) => [row.kind, row]));
  const overrides = new Set((input.view?.turn_overrides ?? []).map((override) => override.kind));
  for (const request of input.requests) {
    if (request.kinds.length === 0) {
      if (input.hasCall)
        entries.push(entry(request.speechActId, null, "vague", { sentence: VAGUE_SENTENCE }));
      continue;
    }
    for (const kind of request.kinds) {
      const decided = decideKind(kind, rows.get(kind), overrides.has(kind), input);
      if (decided === "card") unknown.add(kind);
      else if (decided) entries.push(entry(request.speechActId, kind, decided.outcome, decided));
    }
  }
  if (unknown.size)
    return {
      move: "card",
      kinds: [...unknown],
      entries: input.requests.flatMap((request) =>
        request.kinds
          .filter((kind) => unknown.has(kind))
          .map((kind) => entry(request.speechActId, kind, "card")),
      ),
    };
  return { move: "draft", entries };
}

type KindOutcome =
  | "card"
  | null
  | ({ outcome: MaterialEntry["outcome"] } & Partial<
      Pick<MaterialEntry, "sentence" | "resource_id" | "requirement">
    >);

function decideKind(
  kind: MaterialKind,
  row: MaterialRow | undefined,
  overriddenToCall: boolean,
  input: { hasCall: boolean; sellerResourceUrls: readonly string[] },
): KindOutcome {
  const toCall: KindOutcome = input.hasCall
    ? { outcome: "to_call", sentence: toCallSentence(kind) }
    : null;
  if (overriddenToCall) return toCall;
  const shareablePresent =
    row?.state === "present" && (row.shareable || !BEHIND_THE_CALL.has(kind));
  if (BEHIND_THE_CALL.has(kind) && !shareablePresent) return toCall;
  if (!row) return "card";
  if (row.state === "to_call") return toCall;
  if (row.state === "confirmed_absent")
    return { outcome: "absent", sentence: absentSentence(kind) };
  if (row.delivery === "attachment")
    return {
      outcome: "attach",
      sentence: attachSentence(kind),
      requirement: {
        material_id: row.material_id,
        blob_sha256: row.file?.sha256 ?? null,
        delivery: "attachment",
        file_name: row.file?.file_name ?? null,
      },
    };
  const index = row.url === null ? -1 : input.sellerResourceUrls.indexOf(row.url);
  // A link row whose seller resource is gone is asked again rather than shared from nowhere.
  return index < 0 ? "card" : { outcome: "share_link", resource_id: `seller_resource:${index}` };
}

/**
 * Wording that says a file is on this reply. "The file you attached" and "your attachment" are about
 * the lead's mail and never count.
 */
const ATTACHMENT_WORD = /\battach(?:ed|ment|ments|ing)\b/giu;
const ABOUT_THE_LEAD = /\b(?:you|your)\b[^.!?\n]{0,24}$/iu;

export function claimsAttachment(text: string): boolean {
  for (const match of text.matchAll(ATTACHMENT_WORD))
    if (!ABOUT_THE_LEAD.test(text.slice(Math.max(0, match.index - 40), match.index))) return true;
  return false;
}

/** The record's host sentences, each once. */
export function materialSentences(record: MaterialsRecord | undefined): string[] {
  return [
    ...new Set(
      (record?.entries ?? []).flatMap((value) => (value.sentence === null ? [] : [value.sentence])),
    ),
  ];
}

/**
 * The speech acts the record answers in the draft, whose rep questions no longer stop it. Found again
 * by their `send_materials` binding, because a resumed run plans again and may renumber its acts.
 */
export function materialHandledActs(
  record: MaterialsRecord | undefined,
  requests: readonly MaterialRequest[],
): Set<string> {
  const answers = (record?.entries ?? []).some(
    (value) => value.outcome !== "card" && value.outcome !== "attachment_wording",
  );
  return new Set(answers ? requests.map((request) => request.speechActId) : []);
}

/** Whether a plan step is the card's (`material-request-<kinds>-r<n>-c<n>`, a `manual` step). */
export function isMaterialStep(step: { id: string; input: { kind: string } }): boolean {
  return (
    step.input.kind === "manual" &&
    new RegExp(`^${MATERIAL_STEP_PREFIX}-[a-z_-]+-r[0-9]+-c[0-9]+$`, "u").test(step.id)
  );
}

/** The run action's text: what the card asks, in words a rep reads in a plain chore list. */
export function provideMaterialDescription(kinds: readonly MaterialKind[]): string {
  const named = kinds.map((kind) => MATERIAL_KIND_LABEL[kind]).join(" and ");
  return `They asked for a ${named}; nothing is saved for this campaign. Share it, point them to the call, or confirm you don't have one.`;
}
